// vendors
import {
  ChangeDetectionStrategy,
  Component,
  EventEmitter,
  Input,
  OnChanges,
  Output,
  SimpleChanges,
} from '@angular/core';
import { BehaviorSubject, combineLatest, Observable } from 'rxjs';
import { map, distinctUntilChanged, switchMap } from 'rxjs/operators';
//libs
import { NotebookType } from '@mode/notebooks/contract';

// feature
import { GroupedLibraries, NotebookLibrary } from '../../notebook-supported-libraries.types';
import { NotebookSupportedLibrariesService } from '../../notebook-supported-libraries.service';
import { HelpDocsService, getAppName } from '@mode/shared/util-js';

@Component({
  selector: 'mode-notebook-supported-libraries',
  templateUrl: './notebook-supported-libraries.component.html',
  styleUrls: ['./notebook-supported-libraries.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
  providers: [NotebookSupportedLibrariesService],
})
export class NotebookSupportedLibrariesComponent implements OnChanges {
  @Input() environment: NotebookType = NotebookType.Python3;
  @Input() state: { [section: string]: boolean } = {};
  @Output() sectionToggle: EventEmitter<string> = new EventEmitter();

  private _selectedEnvironment$: BehaviorSubject<NotebookType> = new BehaviorSubject(this.environment);

  groupedLibraries$: Observable<Array<GroupedLibraries>>;
  hasSearchTerm$: Observable<boolean>;
  libraries$: Observable<Array<NotebookLibrary>>;
  searchResults$: Observable<Array<NotebookLibrary>>;
  searchTerm$: BehaviorSubject<string> = new BehaviorSubject('');
  readonly appName = getAppName();

  constructor(
    private notebookSupportedLibraryService: NotebookSupportedLibrariesService,
    public helpDocs: HelpDocsService
  ) {
    // The libraries supported by this
    this.libraries$ = this._selectedEnvironment$.pipe(
      switchMap((library) => this.notebookSupportedLibraryService.getLibraries(library))
    );

    // Libraries group by section
    this.groupedLibraries$ = this.libraries$.pipe(map((libraries) => this.groupLibraries(libraries)));

    // Libraries filter by search input
    this.searchResults$ = combineLatest([this.libraries$, this.searchTerm$]).pipe(
      switchMap(([libraries, searchTerm]) => this.notebookSupportedLibraryService.search(searchTerm, libraries))
    );

    this.hasSearchTerm$ = this.searchTerm$.pipe(
      map((searchInput) => searchInput?.length > 0),
      distinctUntilChanged()
    );
  }

  ngOnChanges(changes: SimpleChanges) {
    if (changes && changes['environment']) {
      // Clear the search input
      this.searchTerm$.next('');
      // Set the selected library
      this._selectedEnvironment$.next(this.environment);
    }
  }

  handleSearchLibrariesChanged(searchTerm: string) {
    this.searchTerm$.next(searchTerm);
  }

  isSectionOpen(item: string) {
    return this.state[item];
  }

  toggleSection(item: string) {
    this.sectionToggle.emit(item);
  }

  private groupLibraries(libraries: Array<NotebookLibrary>) {
    return libraries.reduce((a: Array<GroupedLibraries>, lib: NotebookLibrary) => {
      lib.group.forEach((group) => {
        if (!a.find((g) => g.label === group)) {
          a.push({ label: group, libraries: [lib], key: group });
        } else {
          const library = a.find((g) => g.label === group);
          library?.libraries.push(lib);
        }
      });

      return a;
    }, []);
  }
}
